import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, fmtDate, qty, errMsg } from "@/lib/api";

const FIRM_FALLBACK = { name: "[Firma Ünvanınız]", address: "", tax: "", phone: "" };

const Party = ({ title, p }) => (
  <div className="border border-zinc-400 p-3 text-[12px] leading-5">
    <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{title}</div>
    <div className="text-sm font-bold">{p.name}</div>
    <div>{p.address || "—"}</div>
    <div>VD / VKN: {p.tax || "—"}</div>
    <div>Tel: {p.phone || "—"}</div>
  </div>
);

const Cell = ({ label, value, testid }) => (
  <div className="border border-zinc-400 px-3 py-1.5 text-[12px]">
    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">{label}</div>
    <div className="font-num font-semibold break-words" data-testid={testid}>{value || "—"}</div>
  </div>
);

export default function Waybill() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [s, setS] = useState(null);
  const [co, setCo] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/shipments/${id}`).then((r) => setS(r.data)).catch((e) => setErr(errMsg(e)));
    api.get("/company").then((r) => setCo(r.data)).catch(() => setCo({}));
  }, [id]);
  useEffect(() => {
    if (!s || sp.get("print") !== "1") return undefined;
    const t = setTimeout(() => window.print(), 500);
    return () => clearTimeout(t);
  }, [s, sp]);

  if (err) return <div className="flex h-screen flex-col items-center justify-center gap-2" data-testid="waybill-error"><div className="font-display text-2xl font-bold">Belge bulunamadı</div><div className="text-sm text-muted-foreground">{err}</div></div>;
  if (!s || !co) return <div className="flex h-screen items-center justify-center label-eyebrow">Yükleniyor…</div>;
  const FIRM = co.name ? { name: co.name, address: co.address, tax: [co.tax_office, co.tax_no].filter(Boolean).join(" / "), phone: co.phone } : FIRM_FALLBACK;
  const sale = s.kind === "sale";
  const a = s.account || {};
  const acc = { name: a.name || s.account_name, address: a.address, tax: [a.tax_office, a.tax_no].filter(Boolean).join(" / "), phone: a.phone };
  const total = s.items.reduce((t, i) => t + i.qty, 0);

  return (
    <div className="min-h-screen bg-zinc-200 py-8 print:bg-white print:py-0">
      <div className="no-print mx-auto mb-4 flex max-w-[210mm] justify-end gap-2 px-2">
        <Button onClick={() => window.print()} className="rounded-[4px]" data-testid="waybill-print-button"><Printer /> Yazdır</Button>
        <Button variant="outline" onClick={() => window.close()} className="rounded-[4px] bg-white" data-testid="waybill-close-button"><X /> Kapat</Button>
      </div>
      <div className="mx-auto max-w-[210mm] bg-white p-[12mm] text-zinc-900 shadow-xl print:max-w-none print:p-0 print:shadow-none" data-testid="waybill-document">
        <div className="flex items-start justify-between gap-6 border-b-2 border-zinc-900 pb-4">
          <div className="flex items-center gap-3 text-[12px] leading-5">
            {co.logo && <img src={co.logo} alt="Logo" className="max-h-14 max-w-[110px] object-contain" data-testid="waybill-logo" />}
            <div>
              <div className="font-display text-xl font-extrabold">{FIRM.name}</div>
              <div>{FIRM.address || "—"}</div>
              <div>{[FIRM.tax, FIRM.phone].filter(Boolean).join(" · ") || "—"}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl font-extrabold tracking-tight" data-testid="waybill-title">{sale ? "SEVK İRSALİYESİ" : "MAL KABUL FİŞİ"}</div>
            <div className="font-num text-sm">{s.no}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-0 sm:grid-cols-4 print:grid-cols-4">
          <Cell label={sale ? "İrsaliye No" : "Fiş No"} value={s.waybill_no || s.no} testid="waybill-no" />
          <Cell label="Düzenleme Tarihi" value={fmtDate(s.created_at)} />
          <Cell label={sale ? "Sevk Tarihi" : "Teslim Tarihi"} value={fmtDate(s.date)} testid="waybill-date" />
          <Cell label="Sipariş No / Tarih" value={`${s.order_no} · ${fmtDate(s.order_date)}`} />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <Party title="Gönderen" p={sale ? FIRM : acc} />
          <Party title="Alıcı" p={sale ? acc : FIRM} />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-0 sm:grid-cols-3 print:grid-cols-3">
          <Cell label="Araç Plakası" value={s.vehicle_plate} testid="waybill-plate" />
          <Cell label={sale ? "Şoför / Teslim Eden" : "Teslim Eden"} value={s.driver} testid="waybill-driver" />
          <Cell label={sale ? "Teslimat Adresi" : "Teslim Yeri"} value={s.address || acc.address} testid="waybill-address" />
        </div>

        <table className="mt-5 w-full border-collapse text-[12px]" data-testid="waybill-items">
          <thead>
            <tr className="bg-zinc-900 text-white print:bg-zinc-200 print:text-zinc-900">
              {["Sıra", "Stok Kodu", "Malın Cinsi", "Miktar", "Birim"].map((h, i) => (
                <th key={h} className={`border border-zinc-500 px-2 py-1.5 font-semibold ${i >= 3 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {s.items.map((it, i) => (
              <tr key={i}>
                <td className="border border-zinc-400 px-2 py-1.5 font-num">{i + 1}</td>
                <td className="border border-zinc-400 px-2 py-1.5 font-num">{it.code}</td>
                <td className="border border-zinc-400 px-2 py-1.5">{it.name}</td>
                <td className="border border-zinc-400 px-2 py-1.5 text-right font-num font-semibold">{qty(it.qty)}</td>
                <td className="border border-zinc-400 px-2 py-1.5 text-right">{it.unit}</td>
              </tr>
            ))}
            <tr className="bg-zinc-100 font-semibold">
              <td colSpan={3} className="border border-zinc-400 px-2 py-1.5 text-right">Toplam ({s.items.length} kalem)</td>
              <td className="border border-zinc-400 px-2 py-1.5 text-right font-num" data-testid="waybill-total-qty">{qty(total)}</td>
              <td className="border border-zinc-400" />
            </tr>
          </tbody>
        </table>

        {s.notes && <div className="mt-4 border border-zinc-400 p-3 text-[12px]"><b>Not:</b> {s.notes}</div>}

        <div className="mt-10 grid grid-cols-2 gap-8 text-[12px]">
          {["Teslim Eden", "Teslim Alan"].map((t) => (
            <div key={t} className="border border-zinc-400 p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{t}</div>
              <div className="mt-2">Ad Soyad:</div>
              <div className="mt-8 border-t border-dashed border-zinc-400 pt-1 text-center text-zinc-500">İmza / Kaşe</div>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center text-[10px] text-zinc-400">Bu belge Bizim Cari ile oluşturulmuştur.</div>
      </div>
    </div>
  );
}
